import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4.css';
import './Homework8.css';

type Task = 'task1' | 'task2' | 'task3' | 'task4';

interface Task1Result {
  result: string;
}

interface Task2Result {
  result: string;
}

interface Task3Result {
  tophat: string;
  segmented: string;
}

interface Task4Result {
  after_closing: string;
  after_opening: string;
  gradient: string;
  result: string;
}

const Homework8: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Task>('task1');
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // Task 1 states
  const [task1Result, setTask1Result] = useState<Task1Result | null>(null);
  const [task1Radius, setTask1Radius] = useState(3);

  // Task 2 states
  const [task2Result, setTask2Result] = useState<Task2Result | null>(null);
  const [task2SeSize, setTask2SeSize] = useState(3);

  // Task 3 states
  const [task3Result, setTask3Result] = useState<Task3Result | null>(null);
  const [task3Radius, setTask3Radius] = useState(40);
  const [task3Threshold, setTask3Threshold] = useState(50);

  // Task 4 states
  const [task4Result, setTask4Result] = useState<Task4Result | null>(null);
  const [task4ClosingRadius, setTask4ClosingRadius] = useState(30);
  const [task4OpeningRadius, setTask4OpeningRadius] = useState(53);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear results helper
  const clearResults = () => {
    setTask1Result(null);
    setTask2Result(null);
    setTask3Result(null);
    setTask4Result(null);
  };

  // Load default image when tab changes
  useEffect(() => {
    const taskToImageMap: Record<Task, number> = {
      'task1': 1,
      'task2': 2,
      'task3': 3,
      'task4': 4
    };
    const imageNum = taskToImageMap[activeTab];
    const imagePath = `/hw8_images/HW8_${imageNum}.png`;
    setOriginalImage(imagePath);
    clearResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadPresetImage = (imageNum: number) => {
    const imagePath = `/hw8_images/HW8_${imageNum}.png`;
    setOriginalImage(imagePath);
    clearResults();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        clearResults();
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = async () => {
    if (!originalImage) {
      alert('Please load an image first!');
      return;
    }

    setProcessing(true);

    try {
      // Get base64 image data
      let imageBase64 = originalImage;
      if (!imageBase64.startsWith('data:')) {
        // It's a path, need to fetch and convert
        const response = await fetch(imageBase64);
        const blob = await response.blob();
        const reader = new FileReader();
        imageBase64 = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      }

      // Prepare request based on active task
      const requestData: any = {
        image: imageBase64,
        task: activeTab
      };

      if (activeTab === 'task1') {
        requestData.radius = task1Radius;
      } else if (activeTab === 'task2') {
        requestData.se_size = task2SeSize;
      } else if (activeTab === 'task3') {
        requestData.radius = task3Radius;
        requestData.threshold = task3Threshold;
      } else if (activeTab === 'task4') {
        requestData.closing_radius = task4ClosingRadius;
        requestData.opening_radius = task4OpeningRadius;
      }

      // Call Firebase function
      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework8',
        requestData,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 300000 // 5 minutes timeout for complex morphological operations (Task 4 with large SE)
        }
      );

      if (response.data.success) {
        if (activeTab === 'task1') {
          setTask1Result({
            result: `data:image/png;base64,${response.data.result}`
          });
        } else if (activeTab === 'task2') {
          setTask2Result({
            result: `data:image/png;base64,${response.data.result}`
          });
        } else if (activeTab === 'task3') {
          setTask3Result({
            tophat: `data:image/png;base64,${response.data.tophat}`,
            segmented: `data:image/png;base64,${response.data.segmented}`
          });
        } else if (activeTab === 'task4') {
          setTask4Result({
            after_closing: `data:image/png;base64,${response.data.after_closing}`,
            after_opening: `data:image/png;base64,${response.data.after_opening}`,
            gradient: `data:image/png;base64,${response.data.gradient}`,
            result: `data:image/png;base64,${response.data.result}`
          });
        }
      } else {
        throw new Error(response.data.error || 'Processing failed');
      }
    } catch (error) {
      console.error('Processing error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error processing image: ${errorMsg}`);
    } finally {
      setProcessing(false);
    }
  };

  const downloadImage = (imageData: string, filename: string) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    link.click();
  };

  return (
    <div className="homework4-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/" className="back-button">
          ← Back to Hub
        </Link>

        <header className="homework4-header">
          <div className="badge">Homework 8</div>
          <h1>Morphological Image Processing</h1>
          <p className="subtitle">Noise removal, edge extraction, segmentation, and textural analysis</p>
        </header>

        {/* Tabs */}
        <div className="hw8-tabs">
          <button
            className={`hw8-tab ${activeTab === 'task1' ? 'active' : ''}`}
            onClick={() => setActiveTab('task1')}
          >
            <div className="tab-content">
              <span className="tab-icon">🔧</span>
              <span className="tab-title">Noise Removal</span>
              <span className="tab-subtitle">Opening + Closing</span>
            </div>
          </button>
          <button
            className={`hw8-tab ${activeTab === 'task2' ? 'active' : ''}`}
            onClick={() => setActiveTab('task2')}
          >
            <div className="tab-content">
              <span className="tab-icon">📐</span>
              <span className="tab-title">Gradient Extraction</span>
              <span className="tab-subtitle">Dilation - Erosion</span>
            </div>
          </button>
          <button
            className={`hw8-tab ${activeTab === 'task3' ? 'active' : ''}`}
            onClick={() => setActiveTab('task3')}
          >
            <div className="tab-content">
              <span className="tab-icon">🌾</span>
              <span className="tab-title">Rice Segmentation</span>
              <span className="tab-subtitle">Top-hat + Threshold</span>
            </div>
          </button>
          <button
            className={`hw8-tab ${activeTab === 'task4' ? 'active' : ''}`}
            onClick={() => setActiveTab('task4')}
          >
            <div className="tab-content">
              <span className="tab-icon">🎯</span>
              <span className="tab-title">Blob Separation</span>
              <span className="tab-subtitle">Textural Segmentation</span>
            </div>
          </button>
        </div>

        {/* Image Loading Controls */}
        <div className="controls-section" style={{ marginTop: '30px' }}>
          <h3>Image</h3>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '15px' }}>
            Default image loaded for this task. You can upload a custom image if needed.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button onClick={() => fileInputRef.current?.click()} className="control-button">
              Upload Custom Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Task-specific Controls */}
        <div className="controls-section">
          <h3>Parameters</h3>

          {activeTab === 'task1' && (
            <div className="parameter-control">
              <label>Disk Radius: {task1Radius}</label>
              <input
                type="range"
                min="1"
                max="10"
                value={task1Radius}
                onChange={(e) => setTask1Radius(parseInt(e.target.value))}
                style={{ width: '100%', maxWidth: '400px' }}
              />
              <p className="parameter-description">
                Apply morphological smoothing using opening followed by closing with a disk structuring element
              </p>
            </div>
          )}

          {activeTab === 'task2' && (
            <div className="parameter-control">
              <label>SE Size: {task2SeSize}x{task2SeSize}</label>
              <select
                value={task2SeSize}
                onChange={(e) => setTask2SeSize(parseInt(e.target.value))}
                style={{ padding: '8px', fontSize: '14px' }}
              >
                <option value="3">3x3</option>
                <option value="5">5x5</option>
                <option value="7">7x7</option>
              </select>
              <p className="parameter-description">
                Apply morphological gradient (dilation − erosion) to highlight edges
              </p>
            </div>
          )}

          {activeTab === 'task3' && (
            <div>
              <div className="parameter-control">
                <label>Disk Radius: {task3Radius}</label>
                <input
                  type="range"
                  min="20"
                  max="60"
                  value={task3Radius}
                  onChange={(e) => setTask3Radius(parseInt(e.target.value))}
                  style={{ width: '100%', maxWidth: '400px' }}
                />
              </div>
              <div className="parameter-control">
                <label>Threshold: {task3Threshold}</label>
                <input
                  type="range"
                  min="0"
                  max="255"
                  value={task3Threshold}
                  onChange={(e) => setTask3Threshold(parseInt(e.target.value))}
                  style={{ width: '100%', maxWidth: '400px' }}
                />
              </div>
              <p className="parameter-description">
                Apply white top-hat transformation (disk SE, r≈40) to correct shading and threshold to segment rice grains
              </p>
            </div>
          )}

          {activeTab === 'task4' && (
            <div>
              <div className="parameter-control">
                <label>Closing Radius (remove small blobs): {task4ClosingRadius}</label>
                <input
                  type="range"
                  min="10"
                  max="50"
                  value={task4ClosingRadius}
                  onChange={(e) => setTask4ClosingRadius(parseInt(e.target.value))}
                  style={{ width: '100%', maxWidth: '400px' }}
                />
              </div>
              <div className="parameter-control">
                <label>Opening Radius (remove light patches): {task4OpeningRadius}</label>
                <input
                  type="range"
                  min="30"
                  max="100"
                  value={task4OpeningRadius}
                  onChange={(e) => setTask4OpeningRadius(parseInt(e.target.value))}
                  style={{ width: '100%', maxWidth: '400px' }}
                />
              </div>
              <p className="parameter-description">
                Textural segmentation: closing (r≈30) → opening (r≈60) → gradient (3×3 SE) → superimpose boundary
              </p>
              <div style={{
                backgroundColor: '#dbeafe',
                padding: '12px',
                borderRadius: '8px',
                marginTop: '10px',
                border: '1px solid #3b82f6'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#1e3a8a' }}>
                  ℹ️ <strong>Processing Time:</strong> Task 4 with recommended radius values (30/60) may take 2-3 minutes due to large structuring elements. Please be patient.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={processImage}
            disabled={!originalImage || processing}
            className="control-button"
            style={{
              marginTop: '20px',
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: processing ? '#ccc' : '#3b82f6',
              cursor: processing ? 'not-allowed' : 'pointer'
            }}
          >
            {processing
              ? (activeTab === 'task4'
                  ? 'Processing... (this may take 2-3 minutes)'
                  : 'Processing...')
              : 'Process Image'}
          </button>

          {processing && activeTab === 'task4' && (
            <div style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: '#f0f9ff',
              borderRadius: '6px',
              border: '1px solid #bae6fd',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#0c4a6e' }}>
                ⏳ Processing large morphological operations... Please wait, this can take up to 3 minutes.
              </p>
            </div>
          )}
        </div>

        {/* Results Display */}
        <div className="results-section" style={{ marginTop: '40px' }}>
          <h3>Results</h3>

          {/* Task 1 */}
          {activeTab === 'task1' && (
            <div className="hw8-results-grid">
              <div className="result-item">
                <h4>Original Image</h4>
                {originalImage && <img src={originalImage} alt="Original" />}
              </div>
              <div className="result-item">
                <h4>Noise Removed</h4>
                {task1Result ? (
                  <>
                    <img src={task1Result.result} alt="Result" />
                    <button
                      onClick={() => downloadImage(task1Result.result, 'HW8_1_result.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Task 2 */}
          {activeTab === 'task2' && (
            <div className="hw8-results-grid">
              <div className="result-item">
                <h4>Original Image</h4>
                {originalImage && <img src={originalImage} alt="Original" />}
              </div>
              <div className="result-item">
                <h4>Gradient (Edges)</h4>
                {task2Result ? (
                  <>
                    <img src={task2Result.result} alt="Gradient" />
                    <button
                      onClick={() => downloadImage(task2Result.result, 'HW8_2_result.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Task 3 */}
          {activeTab === 'task3' && (
            <div className="hw8-results-grid-3">
              <div className="result-item">
                <h4>Original Image</h4>
                {originalImage && <img src={originalImage} alt="Original" />}
              </div>
              <div className="result-item">
                <h4>Top-hat Transform</h4>
                {task3Result ? (
                  <>
                    <img src={task3Result.tophat} alt="Top-hat" />
                    <button
                      onClick={() => downloadImage(task3Result.tophat, 'HW8_3_tophat.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
              <div className="result-item">
                <h4>Segmented Rice</h4>
                {task3Result ? (
                  <>
                    <img src={task3Result.segmented} alt="Segmented" />
                    <button
                      onClick={() => downloadImage(task3Result.segmented, 'HW8_3_result.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Task 4 */}
          {activeTab === 'task4' && (
            <div className="hw8-results-grid-4">
              <div className="result-item">
                <h4>Original Image</h4>
                {originalImage && <img src={originalImage} alt="Original" />}
              </div>
              <div className="result-item">
                <h4>After Closing</h4>
                {task4Result ? (
                  <img src={task4Result.after_closing} alt="After Closing" />
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
              <div className="result-item">
                <h4>After Opening</h4>
                {task4Result ? (
                  <img src={task4Result.after_opening} alt="After Opening" />
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
              <div className="result-item">
                <h4>Final Result</h4>
                {task4Result ? (
                  <>
                    <img src={task4Result.result} alt="Final" />
                    <button
                      onClick={() => downloadImage(task4Result.result, 'HW8_4_result.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Process image to see result</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Homework8;
